import LineStoreIcon from 'LuxKit/Icons/line/LineStoreIcon'
import React, { useContext, useRef } from 'react'
import { useIntl } from 'react-intl'
import NavBarButton from '../NavBarButton/NavBarButton'
import ModalContext from 'contexts/ModalContext'
import Modal from 'LuxKit/Modal/Modal'
import DropdownSheet from 'LuxKit/Dropdown/Sheet/DropdownSheet'
import usePreferredRetailStore from 'hooks/usePreferredRetailStore'
import shouldRenderTimely, { getTimelyLink } from 'lib/timely/timely'
import GeoContext from 'contexts/geoContext'
import useToggle from 'hooks/useToggle'
import { useScreenSizeOnly } from 'hooks/useScreenSize'
import NavBarStoreDropdownContent from './NavBarStoreDropdownContent'
import { STORE_CAMPAIGN_URL } from 'constants/retailStores'

interface Props {
  className?: string
}

function NavBarStoreButton(props: Props) {
  const { className } = props
  const intl = useIntl()
  const { currentRegionCode } = useContext(GeoContext)
  const preferredStore = usePreferredRetailStore()
  const { value: isDropdownOpen, toggle: toggleDropdown, off: closeDropdown } = useToggle()
  const buttonRef = useRef<HTMLButtonElement>(null)
  const isMobile = useScreenSizeOnly('mobile')
  const showModal = useContext(ModalContext)

  const visitUsText = intl.formatMessage({
    id: 'axt3EY45giTIyaPlFm4R',
    defaultMessage: 'Visit us',
    description: 'Visit us button label in the navigation bar in the navigation bar',
  })

  const visitUsInStoreText = intl.formatMessage({
    id: 'BttOVF_GdYhVOroXr1zz',
    defaultMessage: 'Visit us in-store',
    description: 'Visit us in-store modal and dropdown title dialog window',
  })

  const bookFreeAppointmentText = intl.formatMessage({
    id: 'I3w6L25mkHIaSgfzfNhd',
    defaultMessage: 'Book free appointment',
    description: 'Book free appointment button text in the navigation bar',
  })

  const learnMoreText = intl.formatMessage({
    id: '-FMWIYhrIUMSByKgQPNW',
    defaultMessage: 'Learn more',
    description: 'Button text to learn more about a feature, offer, or promotional content section',
  })

  const handleClick = () => {
    if (!preferredStore) return

    if (isMobile) {
      showModal((resolve) => (
        <Modal
          title={visitUsInStoreText}
          actionsLayout="stacked"
          primaryActionText={bookFreeAppointmentText}
          primaryActionHref={getTimelyLink()}
          primaryActionTarget="_blank"
          secondaryActionText={learnMoreText}
          secondaryActionTo={STORE_CAMPAIGN_URL}
          onSecondaryActionClick={resolve}
        >
          <NavBarStoreDropdownContent storeType={preferredStore} />
        </Modal>
      ))
    } else {
      toggleDropdown()
    }
  }

  if (!shouldRenderTimely(currentRegionCode) || !preferredStore) {
    return null
  }

  return (
    <>
      <NavBarButton
        className={className}
        icon={<LineStoreIcon />}
        onClick={handleClick}
        ref={buttonRef}
        size="small"
      >
        {visitUsText}
      </NavBarButton>
      <DropdownSheet
        size="S"
        open={!isMobile && isDropdownOpen}
        onClose={closeDropdown}
        anchorRef={buttonRef}
        triggerRef={buttonRef}
        title={visitUsInStoreText}
        hasDismissButton
        autoHeight
        noPaddingOnBody
        secondaryActionProps={{
          kind: 'tertiary',
          children: learnMoreText,
          to: STORE_CAMPAIGN_URL,
          onClick: closeDropdown,
        }}
        primaryActionProps={{
          children: bookFreeAppointmentText,
          href: getTimelyLink(),
          target: '_blank',
          rel: 'noopener',
          onClick: closeDropdown,
        }}
      >
        <NavBarStoreDropdownContent storeType={preferredStore} />
      </DropdownSheet>
    </>
  )
}

export default React.memo(NavBarStoreButton)
